import type { PartDefinition } from "@rogue/rackbuilder"
import { BARS } from "../../modelPaths"
import type { PlateOptions, BarConfig } from "../../RackTypes"

export enum PlateAmount {
  HeavyPlates,
  LightPlates,
  AllPlates
}

export class BarBuilder {
  constructor(private config: BarConfig) {}

  public static createPlates(plateConfig: PlateOptions, showPlateSet?: PlateAmount, left?: boolean, initialOffset?: number) {
    const THICKNESS: {[key: string] : {[key: string] : number}} = {
      hg   : { 45: 2.15, 35: 1.7, 25: 1.15, 15: 0.9, 10: 0.7 },
      echo : { 45: 2.1, 35: 1.5, 25: 1, 15: 0.9, 10: 0.83 },
    }

    const plates: PartDefinition[] = []
    let configType = plateConfig

    if (showPlateSet === PlateAmount.HeavyPlates) {
      // Keeps only the 45lb and 35lb plates in the plateConfig
      configType = plateConfig?.slice(0, 2)
    } else if (showPlateSet === PlateAmount.LightPlates) {
      // Removes the 45lb and 35lb plates from the config
      configType = plateConfig?.length === 4 ? plateConfig?.slice(2, 4) : plateConfig?.slice(2, 5)
      // Add all plates
    } else {
      configType = plateConfig
    }

    let offset = initialOffset || 0
    configType?.forEach((p) => {
      plates.push({
        mesh   : `${BARS}#${p.line}_${p.weight}`,
        slots  : {},
        offset : { x: left ? -offset : offset, y: 0, z: 0 },
      })
      // @todo vary by plate thickness...
      offset += THICKNESS[p.line]?.[p.weight] || 2.2
    })

    return plates
  }

  public build(): PartDefinition {
    const barMaterials: PartDefinition['materials'] = {
      bar: [
        { name: 'bar', ...this.config.shaft },
        { name: 'knurl', ...this.config.knurl },
      ],
    }

    const sleeveMaterials: PartDefinition['materials'] = {
      sleeve: [
        { name: 'endcap', color: '#ffffff', config: { maps: { map: `https://assets.roguefitness.com/w_512,q_auto,f_auto/glb/txt/endcaps/${this.config.endcap || this.config.barType}.jpg` } } },
        { ...this.config.sleeves, name: 'sleeve' },
        { ...this.config.bushing, name: 'bushing' },
      ],
    }

    const leftPlates  = BarBuilder.createPlates(this.config.plates, undefined, true)
    const rightPlates = BarBuilder.createPlates(this.config.plates)

    const leftSleeve = {
      mesh      : `${BARS}#${this.config.barType}_sleeve`,
      slots     : {},
      materials : sleeveMaterials,
    }

    const rightSleeve: PartDefinition = {
      mesh        : `${BARS}#${this.config.barType}_sleeve`,
      slots       : {},
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      materials   : sleeveMaterials,
    }

    const leftSide: PartDefinition[] = [leftSleeve]
    const rightSide: PartDefinition[] = [rightSleeve]

    const bar = {
      mesh  : `${BARS}#${this.config.barType}_bar`,
      slots : {
        left_plate  : leftSide,
        right_plate : rightSide,
      },
      materials: barMaterials,
    }

    bar.slots.left_plate = [...leftSide, ...leftPlates]
    bar.slots.right_plate = [...rightSide, ...rightPlates]

    if (this.config.endcap === 'rogue2.0') {
      bar.slots.left_plate.push({ mesh: `${BARS}#roguebar band`, slots: {} })
      bar.slots.right_plate.push({ mesh: `${BARS}#roguebar band`, slots: {}, adjustments: [{ type: 'rotate', axis: 'Y', amount: 180 }] })
    }

    return bar
  }
}
